"use client"

import { AnimatePresence, motion, useReducedMotion } from "framer-motion"
import { useState } from "react"

interface AnimatedNumberProps {
    /** Target value; when it changes, the displayed value rolls up (increase) or down (decrease). */
    value: number
    /** Formats the value for display (e.g. `formatChf`, de-CH thousands). */
    format?: (value: number) => string
    className?: string
}

const defaultFormat = (value: number) => String(Math.round(value))

/**
 * Direction-aware rolling number for live KPIs. When `value` changes, the new formatted value slides
 * into place — upward for an increase, downward for a decrease — while the previous value slides out,
 * so even a `+1` live tick reads as an animated change rather than an instant jump (a plain count-up
 * tween can't: there are no integers between e.g. 112 and 113 to render). An invisible in-flow copy
 * reserves the box size so the absolutely-positioned roll never shifts surrounding layout. Respects
 * prefers-reduced-motion (renders the value without motion).
 */
export default function AnimatedNumber({ value, format = defaultFormat, className }: AnimatedNumberProps) {
    const reduceMotion = useReducedMotion()
    const formatted = format(value)
    // Roll ONLY when the *displayed* text changes. Live polls jitter the raw value within the same
    // rounded display (e.g. cost 2.01 → 2.02 both render "CHF 2.0", a median 75.213 → 75.216 both
    // "75.21 ms") — those must not animate. We key the roll on `formatted` and track the previous
    // formatted string + value (the latter only to pick the up/down direction). State is adjusted
    // during render (no refs read during render, no setState in effects).
    const [prev, setPrev] = useState({ value, formatted })
    const [direction, setDirection] = useState(1)
    if (formatted !== prev.formatted) {
        setDirection(value >= prev.value ? 1 : -1)
        setPrev({ value, formatted })
    }

    if (reduceMotion) {
        return <span className={className}>{formatted}</span>
    }

    return (
        <span className={`relative inline-block overflow-hidden ${className ?? ""}`}>
            <span className="invisible" aria-hidden="true">
                {formatted}
            </span>
            <AnimatePresence initial={false}>
                <motion.span
                    key={formatted}
                    className="absolute inset-0 whitespace-nowrap"
                    initial={{ y: `${direction * 100}%`, opacity: 0 }}
                    animate={{ y: "0%", opacity: 1 }}
                    exit={{ y: `${direction * -100}%`, opacity: 0 }}
                    transition={{ type: "spring", stiffness: 320, damping: 30, opacity: { duration: 0.18 } }}
                >
                    {formatted}
                </motion.span>
            </AnimatePresence>
        </span>
    )
}
